/**
 * The shared accounts app, which owns sign-in for the whole suite.
 *
 * A session that expires while the dashboard is open is handed off to it with a
 * `return` param, so signing back in lands the user on the page they lost.
 */

import * as Sentry from '@sentry/react';

import { RETURN_PARAM } from 'utils/return-url';
import { isDevelopment } from 'utils/environment';

export const LOCAL_ACCOUNTS_URL = 'http://localhost:5173';

export const DEPLOYED_ACCOUNTS_URL = 'https://accounts.sweat440.dev';

export const getAccountsUrl = () =>
  process.env.REACT_APP_ACCOUNTS_URL ||
  (isDevelopment() ? LOCAL_ACCOUNTS_URL : DEPLOYED_ACCOUNTS_URL);

/** The full URL of the page the user is currently on. */
export const currentUrl = () => window.location.href;

/**
 * Throws when the configured accounts URL is not a valid absolute URL, so a bad
 * REACT_APP_ACCOUNTS_URL surfaces instead of silently navigating nowhere.
 */
export const buildAccountsLoginUrl = (returnUrl) => {
  const url = new URL(getAccountsUrl());

  if (returnUrl) {
    url.searchParams.set(RETURN_PARAM, returnUrl);
  }

  return url.toString();
};

/**
 * The same URL, but a misconfigured REACT_APP_ACCOUNTS_URL comes back as null
 * instead of throwing — for callers that cannot handle an exception, such as a
 * React render, and that have a fallback of their own. The bad configuration is
 * reported to Sentry so it does not go unnoticed.
 */
export const resolveAccountsLoginUrl = (returnUrl) => {
  try {
    return buildAccountsLoginUrl(returnUrl);
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

/**
 * Leaves the app for the accounts sign-in page. Replaces the history entry
 * rather than pushing one: the page being left has a dead session, so Back
 * should not return to it.
 */
export const redirectToAccountsLogin = (returnUrl) => {
  window.location.replace(buildAccountsLoginUrl(returnUrl));
};
